import React, { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { KnowledgeAttachmentPicker } from "../components/KnowledgeAttachmentPicker";
import { api, type GroupTalkMemberRecommendationDto, type GroupTalkMemberRecommendationResponse, type KnowledgeItemData } from "../lib/api";
import { logError } from "../lib/errors";
import {
  buildGroupTalkPersonaKey,
  buildParticipantsFromTemplate,
  createParticipant,
  getGroupTalkAvatarKey,
  getGroupTalkDisplayLabel,
  getGroupTalkDisplayMode,
  getGroupTalkImageVariantFromPersonaKey,
  groupTalkStanceOptions,
  groupTalkTemplates,
  normalizeGroupTalkMemberId,
  normalizeGroupTalkStanceId,
  type GroupTalkParticipant,
  type GroupTalkRoleStanceOption,
  type GroupTalkTemplateId,
} from "../lib/groupTalk";
import {
  loadGroupTalkDraft,
  saveGroupTalkDraft,
  type GroupTalkDraft,
  type GroupTalkKnowledgeAttachmentDraft,
} from "../lib/groupTalkSessionStorage";
import {
  getKnowledgeItemDisplayName,
  getKnowledgeStatusLabel,
  isKnowledgeItemGroupTalkUsable,
} from "../lib/knowledge";

type GroupTalkHubRouteState = {
  errorMessage?: string;
  draft?: GroupTalkDraft;
};

const suggestionExamples = [
  "主力商品の売上が上がらない",
  "新機能の優先順位を相談したい",
  "契約書のリスクを見たい",
  "価格を見直したい",
];

function findTemplate(templateId: GroupTalkTemplateId) {
  return groupTalkTemplates.find((template) => template.id === templateId) ?? groupTalkTemplates[0];
}

function normalizeOptionalString(value: string | null | undefined): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

// スタンス変更UI用に、既存の基本スタンス全 8 種を候補として渡す（テンプレート由来ではない）。
function recommendationStanceOptions(): GroupTalkRoleStanceOption[] {
  return groupTalkStanceOptions.map((stance) => ({ id: stance.id, label: stance.label, description: "" }));
}

function isFacilitatorRecommendation(member: GroupTalkMemberRecommendationDto): boolean {
  const roleKey = typeof member.role_key === "string" ? member.role_key.trim().toLowerCase() : "";
  const personaKey = typeof member.persona_key === "string" ? member.persona_key.trim().toLowerCase() : "";
  return roleKey === "mei" || roleKey === "facilitator" || personaKey.startsWith("mei");
}

function buildMemberRecommendationParticipants(
  response: GroupTalkMemberRecommendationResponse
): GroupTalkParticipant[] {
  const members = Array.isArray(response.recommended_members) ? response.recommended_members : [];
  const experts = members
    .filter((member) => !isFacilitatorRecommendation(member))
    .map((member) => {
      const memberId = normalizeGroupTalkMemberId(member.role_key);
      const imageVariant = getGroupTalkImageVariantFromPersonaKey(member.persona_key);
      const displayName =
        normalizeOptionalString(member.display_name) ?? getGroupTalkDisplayLabel(memberId, "日向 メイ", "named");
      return createParticipant(memberId, normalizeGroupTalkStanceId(member.stance_key), {
        fixed: false,
        availableStances: recommendationStanceOptions(),
        displayRole: normalizeOptionalString(member.role_label),
        displayName,
        // persona/avatar 整合(PR8A)を壊さないため personaKey は既存規約に合わせて生成する。
        personaKey: buildGroupTalkPersonaKey(memberId, displayName),
        avatarKey: getGroupTalkAvatarKey(memberId, imageVariant),
        imageVariant,
        reason: normalizeOptionalString(member.reason),
      });
    })
    .filter(
      (participant, index, items) =>
        index === items.findIndex((item) => item.memberId === participant.memberId)
    );

  return [
    createParticipant("mei", null, {
      fixed: true,
      personaKey: buildGroupTalkPersonaKey("mei"),
      avatarKey: getGroupTalkAvatarKey("mei"),
      imageVariant: 1,
    }),
    ...experts,
  ];
}

function buildMemberRecommendationDraft(
  response: GroupTalkMemberRecommendationResponse,
  consultationText: string,
  knowledgeAttachment: KnowledgeItemData | null
): GroupTalkDraft {
  const participants = buildMemberRecommendationParticipants(response);
  const title = normalizeOptionalString(response.suggested_title) ?? "相談メンバーの提案";
  const description =
    normalizeOptionalString(response.main_issue) ??
    normalizeOptionalString(response.recommended_flow) ??
    "相談内容に合わせて、メイが編成をおすすめしています。";

  return {
    // テンプレート枠に縛られない自由編成。session作成では neutral な "custom" を送る。
    templateId: "custom",
    title,
    description,
    participants,
    mode: "recommend",
    consultationText,
    // 全体理由はメンバーごとの reason で示すため、上部の理由ボックスは使わない。
    recommendationReason: "",
    memberEditingLocked: false,
    knowledgeAttachment: knowledgeAttachmentFromItem(knowledgeAttachment),
  };
}

function knowledgeAttachmentFromItem(item: KnowledgeItemData | null): GroupTalkKnowledgeAttachmentDraft | null {
  if (!item) return null;
  if (item.type !== "pdf" && item.type !== "executive_memo" && item.type !== "url") return null;
  const type = item.type === "url" ? "url" : item.type === "executive_memo" ? "executive_memo" : "pdf";
  return {
    id: item.id,
    type,
    title: getKnowledgeItemDisplayName(item),
    originalFilename: item.original_filename,
    status: item.status,
    failureReason: item.failure_reason,
    pageCount: item.page_count,
    textExtractable: item.text_extractable,
    aiProcessingAllowed: item.ai_processing_allowed,
    aiPreparationStatus: item.ai_preparation_status ?? null,
    hasSummary: Boolean(item.has_summary),
    summarizedAt: item.summarized_at ?? null,
  };
}

function restoreKnowledgeAttachmentFlag(
  attachment: GroupTalkKnowledgeAttachmentDraft,
  key: "textExtractable" | "aiProcessingAllowed",
  type: string
): boolean | null {
  if (!Object.prototype.hasOwnProperty.call(attachment, key)) {
    const effectiveStatus = attachment.status ?? "ready";
    return effectiveStatus === "ready" && (type === "pdf" || type === "executive_memo") ? true : null;
  }
  const value = attachment[key];
  if (typeof value === "boolean") return value;
  return null;
}

function knowledgeItemFromDraft(attachment: GroupTalkKnowledgeAttachmentDraft | null | undefined): KnowledgeItemData | null {
  if (!attachment) return null;
  const type = typeof attachment.type === "string" && attachment.type.trim() ? attachment.type : "pdf";
  if (type !== "pdf" && type !== "executive_memo" && type !== "url") return null;
  return {
    id: attachment.id,
    type,
    title: attachment.title,
    status: attachment.status ?? "ready",
    visibility: "private",
    original_filename: attachment.originalFilename ?? null,
    mime_type: type === "pdf" ? "application/pdf" : null,
    size_bytes: null,
    page_count: attachment.pageCount ?? null,
    text_extractable: restoreKnowledgeAttachmentFlag(attachment, "textExtractable", type),
    ai_processing_allowed: restoreKnowledgeAttachmentFlag(attachment, "aiProcessingAllowed", type),
    ai_preparation_status: attachment.aiPreparationStatus ?? null,
    failure_reason: attachment.failureReason ?? null,
    failure_code: attachment.failureReason ?? null,
    failure_stage: null,
      failure_message: null,
      can_retry: false,
    summary: null,
    has_summary: Boolean(attachment.hasSummary),
    summarized_at: attachment.summarizedAt ?? null,
    created_at: null,
    updated_at: null,
  };
}

function buildKnowledgeAttachmentBlockedMessage(item: KnowledgeItemData): string {
  const displayName = getKnowledgeItemDisplayName(item);
  if (item.status === "failed") {
    return `参考資料「${displayName}」は読み取りに失敗したため、この資料を使った開始はできません。`;
  }

  return `参考資料「${displayName}」は現在「${getKnowledgeStatusLabel(item.status)}」の状態です。利用可能になるまで、この資料を使った開始はできません。`;
}

function buildTemplateDraft(
  templateId: GroupTalkTemplateId,
  consultationText: string,
  knowledgeAttachment: KnowledgeItemData | null
): GroupTalkDraft {
  const template = findTemplate(templateId);
  const mode = templateId === "custom" ? "custom" : "template";
  const displayMode = getGroupTalkDisplayMode(mode);

  return {
    templateId,
    title: template.title,
    description: template.description,
    participants: buildParticipantsFromTemplate(templateId).map((participant) => {
      const imageVariant = mode === "custom" ? 2 : 1;
      const displayName =
        participant.memberId === "mei" ? undefined : getGroupTalkDisplayLabel(participant.memberId, "日向 メイ", displayMode);
      return {
        ...participant,
        fixed: participant.memberId === "mei",
        displayName,
        personaKey: buildGroupTalkPersonaKey(participant.memberId, displayName),
        avatarKey: getGroupTalkAvatarKey(participant.memberId, imageVariant),
        imageVariant,
      };
    }),
    mode,
    consultationText,
    memberEditingLocked: false,
    knowledgeAttachment: knowledgeAttachmentFromItem(knowledgeAttachment),
  };
}

export default function GroupTalkHub() {
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = (typeof location.state === "object" && location.state ? location.state : {}) as GroupTalkHubRouteState;
  const restoredDraft = useMemo(() => routeState.draft ?? loadGroupTalkDraft(), [routeState.draft]);
  const [consultationText, setConsultationText] = useState(restoredDraft?.consultationText ?? "");
  const [knowledgeAttachment, setKnowledgeAttachment] = useState<KnowledgeItemData | null>(
    knowledgeItemFromDraft(restoredDraft?.knowledgeAttachment)
  );
  const [routeError, setRouteError] = useState(routeState.errorMessage ?? "");
  const [recommendError, setRecommendError] = useState("");
  const [recommending, setRecommending] = useState(false);
  const canStartWithAttachment = knowledgeAttachment === null || isKnowledgeItemGroupTalkUsable(knowledgeAttachment);

  const openPreview = (draft: GroupTalkDraft) => {
    saveGroupTalkDraft(draft);
    navigate("/talk/group/preview", {
      state: { draft },
    });
  };

  const changeKnowledgeAttachment = (item: KnowledgeItemData | null) => {
    setKnowledgeAttachment(item);
    setRecommendError("");
    setRouteError("");
    const draft = restoredDraft ?? buildTemplateDraft("custom", consultationText.trim(), item);
    saveGroupTalkDraft({
      ...draft,
      consultationText,
      knowledgeAttachment: knowledgeAttachmentFromItem(item),
    });
  };

  const handleRecommend = async () => {
    const trimmed = consultationText.trim();
    if (!trimmed || recommending) return;
    if (!canStartWithAttachment) {
      setRecommendError("参考資料が利用可能になってから開始できます。資料を外すと、資料なしで始められます。");
      return;
    }

    setRecommending(true);
    setRecommendError("");
    setRouteError("");

    try {
      const readyKnowledgeItemId =
        knowledgeAttachment && isKnowledgeItemGroupTalkUsable(knowledgeAttachment)
          ? knowledgeAttachment.id
          : undefined;
      const response = await api.recommendGroupTalkMembers({
        theme: trimmed,
        ...(readyKnowledgeItemId ? { knowledgeItemId: readyKnowledgeItemId } : {}),
      });
      openPreview(buildMemberRecommendationDraft(response, trimmed, knowledgeAttachment));
    } catch (errorValue) {
      logError(errorValue);
      setRecommendError(
        "おすすめメンバーを作成できませんでした。相談内容を確認して、もう一度お試しください。自由に選んで進めることもできます。"
      );
    } finally {
      setRecommending(false);
    }
  };

  const handleCustomSelect = () => {
    if (!canStartWithAttachment) {
      setRecommendError("参考資料が利用可能になってから開始できます。資料を外すと、資料なしで始められます。");
      return;
    }
    setRouteError("");
    setRecommendError("");
    openPreview(buildTemplateDraft("custom", consultationText.trim(), knowledgeAttachment));
  };

  return (
    <main className="min-h-screen overflow-x-hidden bg-[#FDF8F5] px-4 py-6 text-[#4A3E39] sm:px-6 sm:py-10">
      <div className="mx-auto w-full max-w-5xl">
        <header className="mb-6 flex items-center justify-between">
          <Link to="/talk" className="text-sm font-bold text-[#A67C52] hover:text-[#7C5A4E]">
            ←戻る
          </Link>
          <p className="text-sm font-bold tracking-tight text-[#7C5A4E]">AI社長室</p>
          <Link to="/home" className="text-xs font-bold text-[#9C867D] hover:text-[#7C5A4E]">
            WizMate
          </Link>
        </header>

        <section className="border-b border-[#EADBC8] pb-6">
          <p className="text-xs font-black uppercase tracking-[0.18em] text-[#A67C52]">AI壁打ちルーム</p>
          <h1 className="mt-3 text-3xl font-black tracking-tight text-[#5D4D46] sm:text-4xl">
            相談したいテーマを選ぶ
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-7 text-[#6B5851]">
            AIメンバーと一緒に、悩みを整理して次の一手を考えます。
          </p>
        </section>

        {routeError ? (
          <p role="alert" className="mt-5 rounded-[1rem] border border-[#F0C8BA] bg-[#FFF7F1] px-4 py-3 text-sm font-bold text-[#C06D5A]">
            {routeError}
          </p>
        ) : null}

        <div className="mt-6 space-y-5">
          <section className="rounded-[1.7rem] border border-[#E2CDBF] bg-white px-5 py-6 shadow-[0_18px_44px_rgba(124,90,78,0.10)] sm:px-6">
            <h2 className="text-lg font-black text-[#5D4D46]">相談内容からおすすめ</h2>
            <p className="mt-2 text-sm leading-6 text-[#6B5851]">
              悩みをそのまま入力すると、相談内容に合わせておすすめのテーマとメンバーを提案します。
            </p>
            <textarea
              value={consultationText}
              onChange={(event) => setConsultationText(event.currentTarget.value)}
              placeholder="例：主力商品の売上が上がらない"
              rows={4}
              disabled={recommending}
              className="mt-4 min-h-[132px] w-full resize-none rounded-[1.2rem] border border-[#EADBC8] bg-[#FFFDFB] px-4 py-3 text-sm leading-6 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#D1907B]/20 disabled:opacity-70"
            />
            <div className="mt-3 flex flex-wrap gap-2">
              {suggestionExamples.map((example) => (
                <button
                  key={example}
                  type="button"
                  onClick={() => setConsultationText(example)}
                  disabled={recommending}
                  className="rounded-full border border-[#EADBC8] bg-[#FFFDFB] px-3 py-1.5 text-xs font-bold text-[#7C5A4E] transition hover:bg-[#FFF7F1] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {example}
                </button>
              ))}
            </div>
            <KnowledgeAttachmentPicker
              value={knowledgeAttachment}
              mode="groupTalk"
              onChange={changeKnowledgeAttachment}
            />
            {!canStartWithAttachment && knowledgeAttachment ? (
              <p className="mt-3 text-sm font-bold text-[#C06D5A]">
                {buildKnowledgeAttachmentBlockedMessage(knowledgeAttachment)}
              </p>
            ) : null}
            {recommendError ? (
              <p role="alert" className="mt-3 text-sm font-bold text-[#C06D5A]">
                {recommendError}
              </p>
            ) : null}
            <div className="mt-5 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => void handleRecommend()}
                disabled={recommending || !consultationText.trim() || !canStartWithAttachment}
                className="min-h-[46px] w-full rounded-full bg-[#5D4D46] px-5 py-2 text-sm font-black text-white transition hover:bg-[#4A3E39] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {recommending ? "おすすめを確認中..." : "おすすめメンバーを確認"}
              </button>
              <button
                type="button"
                onClick={handleCustomSelect}
                disabled={recommending || !canStartWithAttachment}
                className="min-h-[46px] w-full rounded-full border border-[#D9C5B9] bg-[#FFFDFB] px-5 py-2 text-sm font-black text-[#6B5851] transition hover:bg-[#FFF7F1] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                自由に選ぶ
              </button>
            </div>
          </section>

          <section className="rounded-[1.3rem] border border-[#EADBC8] bg-white px-5 py-4 shadow-sm">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-base font-black text-[#5D4D46]">続きから始める</h2>
                <p className="mt-1 text-sm leading-6 text-[#6B5851]">以前の相談を開きます。</p>
              </div>
              <Link
                to="/talk/group/sessions"
                className="inline-flex min-h-[40px] shrink-0 items-center justify-center rounded-full border border-[#D9C5B9] bg-[#FFFDFB] px-5 py-2 text-sm font-black text-[#6B5851] transition hover:bg-[#FFF7F1]"
              >
                進む →
              </Link>
            </div>
          </section>
        </div>
      </div>
    </main>
  );
}
